import { AbsoluteTimeRange } from '@chronosphereio/core';
import { merge } from 'lodash';
import React, { createContext, useContext, useMemo } from 'react';
import { EventDefinition, EventItem, EventPayload, MIN_STEP_SIZE_MS, roundStepInterval } from '@/core';
import { useTimeRange } from '../TimeRangeProviderV2';
import { UseEventQueryOptions, useEventQueries } from './events-query';

export type EventSrv = {
  events: EventItem[] | null;
};

export const EventsContext = createContext<EventSrv | undefined>(undefined);

type UseEventContextArgs = {
  /**
   * If true, will throw an error if no context is found. Otherwise will return undefined.
   */
  strict?: boolean;
};

export function useEventContext({ strict = false }: UseEventContextArgs = {}) {
  const context = useContext(EventsContext);
  if (!context && strict) {
    throw new Error('No event context found (strict mode)');
  }
  return context;
}

export type EventsProviderProps = {
  children: React.ReactNode;
  /**
   * Events to be merged with events from queries.
   */
  events?: EventItem[];

  /**
   * Event definitions to be queried.
   */
  eventDefinitions?: EventDefinition[];
  /**
   * Options for event queries (e.g. step size)
   */
  options?: UseEventQueryOptions;
};

export function EventsProvider({ children, options, events = [], eventDefinitions = [] }: EventsProviderProps) {
  const { absoluteTimeRange } = useTimeRange();

  const eventQueryOptions: UseEventQueryOptions = useMemo(() => {
    const defaultStepSize = roundStepInterval(calculateEventStepSizeInMs({ timeRange: absoluteTimeRange }));
    const defaultOptions = { suggestedStepMs: defaultStepSize };

    return merge(defaultOptions, options);
  }, [options, absoluteTimeRange]);

  const results = useEventQueries(eventDefinitions, eventQueryOptions);

  // merge events from props and queries
  const allEvents = useMemo(() => {
    const queryEvents: EventPayload[] = [];
    for (const result of results) {
      if (result.data?.events) {
        queryEvents.push(...result.data.events);
      }
    }
    return [...events, ...queryEvents];
  }, [results, events]);

  return (
    <EventsContext.Provider
      value={{
        events: allEvents,
      }}
    >
      {children}
    </EventsContext.Provider>
  );
}

const DEFAULT_SECTION_WIDTH = 50;
interface EventStepSize {
  timeRange: AbsoluteTimeRange;
  width?: number;
  sectionWidth?: number;
}

/**
 * Calculate step size in ms based on graph width and time range.
 * @param timeRange the absolute time range of the data
 * @param width the width of the content (default is half of the viewport's width).
 * @param  sectionWidth - The width of each section in pixels (default is 50 pixels).
 * @returns The calculated step size in ms.
 */
export function calculateEventStepSizeInMs({
  timeRange,
  width,
  sectionWidth = DEFAULT_SECTION_WIDTH,
}: EventStepSize): number {
  // use the width of the viewport as default
  const w = width ?? window.innerWidth / 2;

  // Calculate the total number of sections
  const totalSections: number = Math.floor(w / sectionWidth);

  // Calculate the total time range in ms
  const totalRangeInMs: number = timeRange.end.getTime() - timeRange.start.getTime();

  // Calculate step size in ms
  const stepSizeInMs: number = totalRangeInMs / totalSections;
  const roundedStepSizeInMs: number = Math.round(stepSizeInMs);

  return Math.max(roundedStepSizeInMs, MIN_STEP_SIZE_MS);
}
